import * as React from 'react';
import { Theme, createStyles, makeStyles } from '@material-ui/core';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import { PartyCommon, PartyCommonCardProps } from '../../components/Party/PartyCommon';
import { MatterIndividual } from '../parties/PartiesInfo';
import { ApplicationsLabelSettingsConfig } from '../MatterDetails';

// Explicit request: a modern card for each staff member (icon chip + name + role, then the
// address/phone/email panels below), replacing the old Semantic UI RvCard + glamorous theme
// treatment. Colours reuse this app's own --rv-accent-* tokens, matching the indigo chip
// MatterDetailsTabList.tsx already uses for this tab.
const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        card: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            background: '#fff',
            boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.06)',
            borderRadius: 8,
            padding: theme.spacing(2.5),
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.5),
            marginBottom: theme.spacing(2),
        },
        iconChip: {
            width: 44,
            height: 44,
            minWidth: 44,
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            backgroundColor: 'var(--rv-accent-indigo-bg)',
            color: 'var(--rv-accent-indigo)',
        },
        name: {
            fontSize: 15.5,
            fontWeight: 700,
            color: theme.palette.text.primary,
            lineHeight: 1.3,
        },
        role: {
            fontSize: 12.5,
            color: theme.palette.text.secondary,
            marginTop: 2,
        },
        body: {
            display: 'flex',
            flexDirection: 'column',
        },
    })
);

export interface StaffCardProps extends PartyCommonCardProps {
    data: MatterIndividual;
    labelSettingsConfig: ApplicationsLabelSettingsConfig;
}

function isInMapping(matterRole: string, mapping: string | undefined) {
    if (!mapping) {
        return false;
    }
    const role = matterRole.trim().toUpperCase();
    return mapping.split(',').some(m => m.trim().toUpperCase() === role);
}

export function setMatterRoleLabels(matterRole: string | undefined, labelSettings: ApplicationsLabelSettingsConfig) {
    if (matterRole) {
        if (isInMapping(matterRole, labelSettings.matterWorkingResponsibilityMapping)) {
            return labelSettings.personActingLabel || '';
        }
        if (isInMapping(matterRole, labelSettings.matterOversightResponsibilityMapping)) {
            return labelSettings.personResponsibleLabel || '';
        }
        if (isInMapping(matterRole, labelSettings.matterAssistingResponsibilityMapping)) {
            return labelSettings.matterAssistingResponsibilityLabel || '';
        }
        if (isInMapping(matterRole, labelSettings.matterIntroducerResponsibilityMapping)) {
            return labelSettings.matterIntroducingResponsibilityLabel || '';
        }
    }
    return '';
}

export const RvStaffCard: React.FC<StaffCardProps> = (props) => {
    const classes = useStyles();

    const infoPanelItems = new Array<{}>();
    PartyCommon.renderCommon(props, infoPanelItems, 'inline');

    const roleLabel = setMatterRoleLabels(props.data.matterRole, props.labelSettingsConfig);

    return (
        <div className={classes.card}>
            <div className={classes.header}>
                <span className={classes.iconChip}>
                    <PersonOutlineIcon />
                </span>
                <div>
                    <div className={classes.name}>{props.data.displayName}</div>
                    {!!roleLabel && <div className={classes.role}>{roleLabel}</div>}
                </div>
            </div>
            <div className={classes.body}>{infoPanelItems}</div>
        </div>
    );
};
